import get from 'lodash/get';
import { SEARCH_TERM_COLORS, STORIES_PAGE_SIZE } from './helpers';

// the stories in page that aren't in the list yet. Offset pages can overlap when stories
// share a created_at, Bacon's tiebreak sort should prevent it, this is the backstop
const _newStories = (stories, page) => {
  const listed = new Set(stories.map(story => story.story_id));
  return page.filter(story => !listed.has(story.story_id));
};

// Actions
const FETCH_SEARCH_RESULTS = 'zen/DiscoverHistorics/Page/FETCH_SEARCH_RESULTS';
const FETCH_RECENT_SEARCH_RESULTS = 'zen/DiscoverHistorics/Page/FETCH_RECENT_SEARCH_RESULTS';
const FETCH_TRANSLATIONS = 'zen/DiscoverHistorics/Page/FETCH_TRANSLATIONS';
const FETCH_SEARCH_INTERVAL = 'zen/DiscoverHistorics/Page/FETCH_SEARCH_INTERVAL';
const FETCH_SEARCH_RESULTS_SUCCEEDED = 'zen/DiscoverHistorics/Page/FETCH_SEARCH_RESULTS_SUCCEEDED';
const FETCH_TRANSLATIONS_SUCCEEDED = 'zen/DiscoverHistorics/Page/FETCH_TRANSLATIONS_SUCCEEDED';
const FETCH_RECENT_SEARCHES = 'zen/DiscoverHistorics/Page/FETCH_RECENT_SEARCHES';
const FETCH_RECENT_SEARCHES_SUCCEEDED = 'zen/DiscoverHistorics/Page/FETCH_RECENT_SEARCHES_SUCCEEDED';
const UPDATE_SEARCH_HISTORY = 'zen/DiscoverHistorics/Page/UPDATE_SEARCH_HISTORY';
const FETCH_MORE_STORIES = 'zen/DiscoverHistorics/Page/FETCH_MORE_STORIES';
const FETCH_MORE_STORIES_SUCCEEDED = 'zen/DiscoverHistorics/Page/FETCH_MORE_STORIES_SUCCEEDED';
const FETCH_NEW_STORIES = 'zen/DiscoverHistorics/Page/FETCH_NEW_STORIES';
const FETCH_NEW_STORIES_SUCCEEDED = 'zen/DiscoverHistorics/Page/FETCH_NEW_STORIES_SUCCEEDED';
const FETCH_FAILED = 'zen/DiscoverHistorics/Page/FETCH_FAILED';
const FETCH_TRANSLATIONS_FAILED = 'zen/DiscoverHistorics/Page/FETCH_TRANSLATIONS_FAILED';
const SET_TRANSLATION_NOTICE = 'zen/DiscoverHistorics/Page/SET_TRANSLATION_NOTICE';
const CLEAR_FETCH_ERROR = 'zen/DiscoverHistorics/Page/CLEAR_FETCH_ERROR';
const CLEAR_SEARCH_RESULTS = 'zen/DiscoverHistorics/Page/CLEAR_SEARCH_RESULTS';
const SELECT_INTERVAL = 'zen/DiscoverHistorics/Page/SELECT_INTERVAL';
const CLEAR_RECENT_SEARCHES = 'zen/DiscoverHistorics/Page/CLEAR_RECENT_SEARCHES';
const CLEAR_USER_HISTORY_SUCCEEDED = 'zen/DiscoverHistorics/Page/CLEAR_USER_HISTORY_SUCCEEDED';
const FILTER_TERM = 'zen/DiscoverHistorics/Page/FILTER_TERM';
const FILTER_SOURCE = 'zen/DiscoverHistorics/Page/FILTER_SOURCE';
const SAVE_AS_PROFILE = 'zen/DiscoverHistorics/Page/SAVE_AS_PROFILE';
const SHOULD_SCROLL = 'zen/DiscoverHistorics/Page/SHOULD_SCROLL';
const SET_PRIVACY_MODE = 'zen/DiscoverHistorics/Page/SET_PRIVACY_MODE';

const initialState = {
  fetchErrorMessage: null,
  hasFetchErrors: false,
  isFetching: false,
  isFetchingTranslations: false,
  lastSearch: [],
  lastSearchEndDate: null,
  lastSearchStartDate: null,
  timeSeriesCounts: [],
  intervalOptions: [],
  intervalSelectionIndex: 1,
  selectedTerm: {},
  stories: [],
  filters: [],
  hasMoreStories: false,
  recentSearches: [],
  selectedSource: 'allSources',
  loadingMoreStories: false,
  lineChartBackDisabled: true,
  shouldScrollPage: false,
  privacyMode: false
};

// Reducer
// eslint-disable-next-line complexity
const reducer = (state = initialState, action = {}) => {
  const { type, payload } = action;

  switch (type) {
    case CLEAR_FETCH_ERROR:
      return { ...state, fetchErrorMessage: null, hasFetchErrors: false };
    case FETCH_RECENT_SEARCH_RESULTS: {
      return {
        ...state,
        fetchErrorMessage: null,
        hasFetchErrors: false,
        isFetching: true,
        isFetchingTranslations: false,
        timeSeriesCounts: [],
        stories: [],
        hasMoreStories: false,
        // a new search always starts with all sources
        selectedSource: 'allSources',
        lineChartBackDisabled: true,
        isFetchingTranslations: true,
        shouldScrollPage: false
      };
    }
    case FETCH_SEARCH_RESULTS: {
      return {
        ...state,
        fetchErrorMessage: null,
        hasFetchErrors: false,
        isFetching: true,
        isFetchingTranslations: false,
        timeSeriesCounts: [],
        stories: [],
        hasMoreStories: false,
        // a new search always starts with all sources
        selectedSource: 'allSources',
        lineChartBackDisabled: true,
        shouldScrollPage: false
      };
    }
    case FETCH_TRANSLATIONS: {
      return {
        ...state,
        fetchErrorMessage: null,
        hasFetchErrors: false,
        isFetchingTranslations: true,
      };
    }
    case FETCH_SEARCH_INTERVAL: {
      return {
        ...state,
        fetchErrorMessage: null,
        hasFetchErrors: false,
        isFetching: true,
        timeSeriesCounts: [],
      };
    }
    case FETCH_SEARCH_RESULTS_SUCCEEDED: {
      const {
        searchTerms,
        startDate,
        endDate,
        timeSeriesCounts,
        intervalOptions,
        intervalSelectionIndex,
        stories
      } = payload;
      const timeSeriesWithColorAndLabel = timeSeriesCounts
        .map((value, index) => (
          {
            ...value,
            color: SEARCH_TERM_COLORS[index],
            label: searchTerms[index]
          }
        ));

      return {
        ...state,
        fetchErrorMessage: null,
        hasFetchErrors: false,
        isFetching: false,
        lastSearch: searchTerms,
        lastSearchEndDate: endDate,
        lastSearchStartDate: startDate,
        timeSeriesCounts: timeSeriesWithColorAndLabel,
        intervalOptions,
        intervalSelectionIndex,
        stories,
        hasMoreStories: stories.length >= STORIES_PAGE_SIZE,
        loadingMoreStories: false,
      };
    }

    case FETCH_TRANSLATIONS_SUCCEEDED: {
      return {
        ...state,
        isFetchingTranslations: false,
      };
    }

    case FETCH_TRANSLATIONS_FAILED: {
      // FFB errors are displayed differently, so they arrive without a payload
      if (payload) {
        return {
          ...state,
          isFetchingTranslations: false,
          fetchErrorMessage: payload,
          hasFetchErrors: true,
        };
      }
      return {
        ...state,
        isFetchingTranslations: false,
      };
    }

    // informational notice raised on a success path, it must not touch the
    // fetching flags - a search started while the translation was in flight
    // has to keep its spinner
    case SET_TRANSLATION_NOTICE: {
      return {
        ...state,
        fetchErrorMessage: payload,
        hasFetchErrors: true,
      };
    }

    case FETCH_MORE_STORIES_SUCCEEDED: {
      const { stories, replace } = payload;
      if (replace) {
        return {
          ...state,
          fetchErrorMessage: null,
          stories,
          hasMoreStories: stories.length >= STORIES_PAGE_SIZE,
          loadingMoreStories: false,
        };
      }
      const newStories = _newStories(state.stories, stories);
      return {
        ...state,
        fetchErrorMessage: null,
        stories: state.stories.concat(newStories),
        // a page that adds nothing new would page forever
        hasMoreStories: stories.length >= STORIES_PAGE_SIZE && newStories.length > 0,
        loadingMoreStories: false,
      };
    }

    case FETCH_RECENT_SEARCHES:
      return {
        ...state,
        fetchErrorMessage: null,
        recentSearches: [],
      };
    case FETCH_RECENT_SEARCHES_SUCCEEDED: {
      const {
        userId,
        history
      } = payload;
      return {
        ...state,
        userId,
        recentSearches: history,
        fetchErrorMessage: null,
      };
    }
    case UPDATE_SEARCH_HISTORY:
      return {
        ...state,
        fetchErrorMessage: null,
        recentSearches: [],
      };
    case FETCH_NEW_STORIES:
      return {
        ...state,
        loadingMoreStories: true,
      };
    case FETCH_NEW_STORIES_SUCCEEDED: {
      const {
        stories,
        startDate,
        endDate,
        lineChartBackDisabled
      } = payload;
      return {
        ...state,
        stories,
        lastSearchStartDate: startDate,
        lastSearchEndDate: endDate,
        fetchErrorMessage: null,
        hasMoreStories: stories.length >= STORIES_PAGE_SIZE,
        lineChartBackDisabled,
        loadingMoreStories: false,
      };
    }

    // a failed fetch stops paging: after a failed source change the list still holds the
    // old source's stories, and more pages would add the new source's below them
    case FETCH_FAILED:
      if (payload) {
        return {
          ...state,
          fetchErrorMessage: payload,
          isFetching: false,
          hasFetchErrors: true,
          loadingMoreStories: false,
          hasMoreStories: false,
        };
      }
      // FFBerror are displayed differently, so we refresh the state
      return {
        ...state,
        fetchErrorMessage: payload,
        isFetching: false,
        hasFetchErrors: false,
        loadingMoreStories: false,
        hasMoreStories: false,
      };
    case SELECT_INTERVAL:
      return { ...state, intervalSelectionIndex: payload };
    case CLEAR_SEARCH_RESULTS:
      return initialState;
    case CLEAR_RECENT_SEARCHES:
      return state;
    case CLEAR_USER_HISTORY_SUCCEEDED:
      return {
        ...state,
        recentSearches: [],
      };
    case FILTER_TERM: {
      const filters = state.filters;
      const newFilters = filters.includes(payload) ? filters.filter((term)=> term !== payload) : [...filters, payload];
      return { ...state, filters: newFilters };
    }
    case FILTER_SOURCE: {
      return {
        ...state,
        selectedSource: payload.source,
        loadingMoreStories: payload.loadingMoreStories
      };
    }
    case SHOULD_SCROLL: {
      return { ...state, shouldScrollPage: true };
    }
    case SET_PRIVACY_MODE: {
      return { ...state, privacyMode: !!payload };
    }
    default:
      return state;
  }
};

const _pendoTrack = (meta) => {
  const payload = get(meta, 'analytics.eventPayload.properties');
  // eslint-disable-next-line no-undef
  pendo.track('Discover Historics Search', payload);
};

// Action Creators
const fetchSearchResults = () => {
  return { type: FETCH_SEARCH_RESULTS };
};

const fetchRecentSearchResults = (payload) => {
  return { type: FETCH_RECENT_SEARCH_RESULTS, payload };
};

const fetchTranslations = (payload) => {
  return { type: FETCH_TRANSLATIONS, payload };
};

const fetchTranslationsSucceded = () => {
  return { type: FETCH_TRANSLATIONS_SUCCEEDED };
};

const fetchTranslationsFailed = (payload) => {
  return { type: FETCH_TRANSLATIONS_FAILED, payload };
};

const fetchSearchInterval = () => {
  return { type: FETCH_SEARCH_INTERVAL };
};

const fetchSearchResultsSucceeded = (payload, meta) => {
  _pendoTrack(meta);
  return { type: FETCH_SEARCH_RESULTS_SUCCEEDED, payload, meta };
};

const fetchRecentSearches = () => {
  return { type: FETCH_RECENT_SEARCHES };
};

const fetchRecentSearchesSucceeded = (payload) => {
  return {type: FETCH_RECENT_SEARCHES_SUCCEEDED, payload};
};

const updateSearchHistory = () => {
  return { type: UPDATE_SEARCH_HISTORY };
};

// InfiniteScroll calls loadMore with a page number, the object parameter keeps that
// number from being read as a reset
const fetchMoreStories = ({ reset = false } = {}) => {
  return { type: FETCH_MORE_STORIES, reset };
};

const fetchMoreStoriesSucceeded = (payload, meta) => {
  return { type: FETCH_MORE_STORIES_SUCCEEDED, payload, meta };
};

const fetchNewStories = (payload) => {
  return { type: FETCH_NEW_STORIES, payload };
};

const fetchNewStoriesSucceeded = (payload) => {
  return { type: FETCH_NEW_STORIES_SUCCEEDED, payload };
};

const fetchFailed = (payload, meta) => {
  _pendoTrack(meta);
  return { type: FETCH_FAILED, payload, meta };
};

// same banner as fetchFailed, but this is an informational notice on a success
// path - it must not emit a search analytics event and must not cancel any
// fetch that is currently running
const setTranslationNotice = (payload) => {
  return { type: SET_TRANSLATION_NOTICE, payload };
};

const clearFetchError = () => {
  return { type: CLEAR_FETCH_ERROR };
};

const clearSearchResults = () => {
  return { type: CLEAR_SEARCH_RESULTS };
};

const selectInterval = (payload) => {
  return { type: SELECT_INTERVAL, payload };
};

const clearRecentSearches = () => {
  return { type: CLEAR_RECENT_SEARCHES };
};

const clearUserHistorySucceeded = () => {
  return { type: CLEAR_USER_HISTORY_SUCCEEDED };
};

const filterTerm = (payload) => {
  return { type: FILTER_TERM, payload };
};

const filterSource = (payload) => {
  return { type: FILTER_SOURCE, payload };
};

const saveAsProfile = (payload) => {
  return { type: SAVE_AS_PROFILE, payload };
};

const shouldScroll = () => {
  return { type: SHOULD_SCROLL };
};

const setPrivacyMode = (payload) => {
  return { type: SET_PRIVACY_MODE, payload };
};

export default reducer;
export {
  // Actions
  FETCH_SEARCH_RESULTS,
  FETCH_RECENT_SEARCH_RESULTS,
  FETCH_TRANSLATIONS,
  FETCH_SEARCH_INTERVAL,
  FETCH_TRANSLATIONS_SUCCEEDED,
  FETCH_SEARCH_RESULTS_SUCCEEDED,
  FETCH_RECENT_SEARCHES,
  FETCH_RECENT_SEARCHES_SUCCEEDED,
  UPDATE_SEARCH_HISTORY,
  FETCH_MORE_STORIES,
  FETCH_MORE_STORIES_SUCCEEDED,
  FETCH_NEW_STORIES,
  FETCH_NEW_STORIES_SUCCEEDED,
  FETCH_FAILED,
  FETCH_TRANSLATIONS_FAILED,
  SET_TRANSLATION_NOTICE,
  CLEAR_FETCH_ERROR,
  CLEAR_SEARCH_RESULTS,
  SELECT_INTERVAL,
  CLEAR_RECENT_SEARCHES,
  CLEAR_USER_HISTORY_SUCCEEDED,
  SAVE_AS_PROFILE,
  SHOULD_SCROLL,
  SET_PRIVACY_MODE,
  // Action Creators
  fetchSearchResults,
  fetchRecentSearchResults,
  fetchTranslations,
  fetchSearchInterval,
  fetchSearchResultsSucceeded,
  fetchTranslationsSucceded,
  fetchRecentSearches,
  fetchRecentSearchesSucceeded,
  updateSearchHistory,
  fetchMoreStories,
  fetchMoreStoriesSucceeded,
  fetchNewStories,
  fetchNewStoriesSucceeded,
  fetchFailed,
  setTranslationNotice,
  fetchTranslationsFailed,
  clearFetchError,
  clearSearchResults,
  selectInterval,
  clearRecentSearches,
  clearUserHistorySucceeded,
  filterTerm,
  filterSource,
  saveAsProfile,
  shouldScroll,
  setPrivacyMode
};
